import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
  useMemo,
  useCallback
} from "react";
import Drawer from "react-modern-drawer";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  ButtonVariant,
  ButtonSize,
  Show,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import {
  CreatedWithinOption,
  LeadsFilters,
  ShowingWithinOption
} from "../../types";
import {
  LEADS_FILTER_OPTIONS_QUERY_KEY,
  useLeadsFilterOptions
} from "../../hooks/useLeadsFilterOptions";
import {
  APPROVAL_LIKELIHOOD_BANDS,
  APPROVAL_LIKELIHOOD_BAND_LABEL,
  isApprovalLikelihoodBand,
  type ApprovalLikelihoodBand
} from "api/approval-likelihood";
import { FilterSection } from "./FilterSection";
import { PRESCREENING_RESULT_FILTER_OPTIONS } from "shared/app/PrescreeningResult";
import type { PrescreeningResult } from "api/lead";
import { Cancel01Icon } from "@hugeicons-pro/core-solid-rounded";
import {
  CalendarMinus02Icon,
  Calendar03Icon,
  CalendarCheckIn02Icon,
  CalendarCheckOut02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  DateRangeSelect,
  DateRangePreset
} from "shared/components/DateRangeSelect";

const LEADS_DATE_PRESETS: DateRangePreset[] = [
  {
    value: CreatedWithinOption.None,
    label: "Don't filter",
    icon: CalendarMinus02Icon,
    isNone: true
  },
  {
    value: CreatedWithinOption.Last7Days,
    label: "Last 7 days",
    icon: CalendarCheckIn02Icon
  },
  {
    value: CreatedWithinOption.Last30Days,
    label: "Last 30 days",
    icon: CalendarCheckIn02Icon
  },
  {
    value: CreatedWithinOption.Custom,
    label: "Pick a date range",
    icon: Calendar03Icon,
    isCustom: true
  }
];

// Single boolean dressed as a one-option checkbox group so it reads like the
// other filter sections. No count: the BE filter_options payload has no
// waitlist bucket.
const WAITLIST_FILTER_OPTION_ID = "on_the_waitlist";
const WAITLIST_FILTER_OPTIONS = [
  { id: WAITLIST_FILTER_OPTION_ID, name: "On the waitlist" }
];

const SHOWING_DATE_PRESETS: DateRangePreset[] = [
  {
    value: ShowingWithinOption.None,
    label: "Don't filter",
    icon: CalendarMinus02Icon,
    isNone: true
  },
  {
    value: ShowingWithinOption.Last7Days,
    label: "Last 7 days",
    icon: CalendarCheckIn02Icon
  },
  {
    value: ShowingWithinOption.Last30Days,
    label: "Last 30 days",
    icon: CalendarCheckIn02Icon
  },
  {
    value: ShowingWithinOption.Next7Days,
    label: "Next 7 days",
    icon: CalendarCheckOut02Icon
  },
  {
    value: ShowingWithinOption.Next30Days,
    label: "Next 30 days",
    icon: CalendarCheckOut02Icon
  },
  {
    value: ShowingWithinOption.Custom,
    label: "Pick a date range",
    icon: Calendar03Icon,
    isCustom: true
  }
];

interface Props {
  filters?: LeadsFilters;
  setFilters: Dispatch<SetStateAction<LeadsFilters | undefined>>;
  showFilters: boolean;
  setShowFilters: Dispatch<SetStateAction<boolean>>;
  property_ids?: string[];
  unit_ids?: string[];
  search?: string;
  hidePropertyFilter?: boolean;
  isLeadScoringEnabled?: boolean;
}

const APPROVAL_LIKELIHOOD_BAND_OPTIONS = APPROVAL_LIKELIHOOD_BANDS.map(
  (band) => ({
    id: band,
    name: APPROVAL_LIKELIHOOD_BAND_LABEL[band]
  })
);

// Tag names keep the capitalization they were created with.
const keepTagName = (name: string) => name;

export const FiltersDrawer: FC<Props> = ({
  property_ids,
  unit_ids,
  filters,
  setFilters,
  setShowFilters,
  showFilters,
  search,
  hidePropertyFilter,
  isLeadScoringEnabled
}) => {
  const queryClient = useQueryClient();

  const [selectedProperties, setSelectedProperties] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedStages, setSelectedStages] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedLeadSources, setSelectedLeadSources] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedTags, setSelectedTags] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedActivityTypes, setSelectedActivityTypes] = useState<string[]>(
    []
  );
  const [selectedApprovalLikelihoodBands, setSelectedApprovalLikelihoodBands] =
    useState<ApprovalLikelihoodBand[]>([]);
  const [selectedPrescreeningResults, setSelectedPrescreeningResults] =
    useState<PrescreeningResult[]>([]);
  const [onWaitlist, setOnWaitlist] = useState(false);
  const [selectedCreatedWithin, setSelectedCreatedWithin] =
    useState<CreatedWithinOption>(CreatedWithinOption.None);
  const [selectedCreatedWithinDates, setSelectedCreatedWithinDates] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });
  const [selectedShowingWithin, setSelectedShowingWithin] =
    useState<ShowingWithinOption>(ShowingWithinOption.None);
  const [selectedShowingWithinDates, setSelectedShowingWithinDates] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: "", endDate: "" });

  const { data: filterOptions } = useLeadsFilterOptions({
    filters,
    propertyIds: property_ids,
    unitIds: unit_ids,
    search
  });

  const tagOptions = useMemo(
    () =>
      filterOptions?.tags?.map((tag) => ({
        id: String(tag.id),
        name: tag.name,
        count: tag.count
      })) ?? [],
    [filterOptions?.tags]
  );

  const prescreeningResultOptions = useMemo(() => {
    const counts = new Map(
      (filterOptions?.prescreening_results ?? []).map((bucket) => [
        bucket.result,
        bucket.count
      ])
    );
    return PRESCREENING_RESULT_FILTER_OPTIONS.map((option) => ({
      ...option,
      count: counts.get(option.id) ?? 0
    }));
  }, [filterOptions?.prescreening_results]);

  useEffect(() => {
    if (showFilters) {
      queryClient.invalidateQueries({
        queryKey: [LEADS_FILTER_OPTIONS_QUERY_KEY]
      });
      setSelectedProperties(filters?.properties || []);
      setSelectedStages(filters?.stages || []);
      setSelectedLeadSources(filters?.leadSources || []);
      setSelectedTags(filters?.tags || []);
      setSelectedActivityTypes(filters?.activityTypes || []);
      setSelectedApprovalLikelihoodBands(
        filters?.approvalLikelihoodBands || []
      );
      setSelectedPrescreeningResults(filters?.prescreeningResults || []);
      setOnWaitlist(Boolean(filters?.onWaitlist));
      setSelectedCreatedWithin(
        filters?.createdWithin ?? CreatedWithinOption.None
      );
      setSelectedCreatedWithinDates({
        startDate: filters?.createdAfter ?? "",
        endDate: filters?.createdBefore ?? ""
      });
      setSelectedShowingWithin(
        filters?.showingWithin ?? ShowingWithinOption.None
      );
      setSelectedShowingWithinDates({
        startDate: filters?.showingAfter ?? "",
        endDate: filters?.showingBefore ?? ""
      });
    }
  }, [showFilters, filters, queryClient]);

  const toggleFilter = useCallback(
    (
      filterId: string,
      allOptions: { id: string; name: string }[] | undefined,
      setter: React.Dispatch<
        React.SetStateAction<{ id: string; name: string }[]>
      >
    ) => {
      setter((prev) =>
        prev.some((item) => item.id === filterId)
          ? prev.filter((item) => item.id !== filterId)
          : [
              ...prev,
              {
                id: filterId,
                name: allOptions?.find((opt) => opt.id === filterId)?.name ?? ""
              }
            ]
      );
    },
    []
  );

  const applyFilters = () => {
    setFilters((prev) => ({
      properties: selectedProperties,
      // The drawer has no unit selector; units only arrives via deep link and
      // must survive an Apply, same as leadTypes below.
      units: prev?.units,
      stages: selectedStages,
      leadSources: selectedLeadSources,
      tags: selectedTags,
      leadTypes: prev?.leadTypes,
      activityTypes: selectedActivityTypes,
      approvalLikelihoodBands: isLeadScoringEnabled
        ? selectedApprovalLikelihoodBands
        : prev?.approvalLikelihoodBands,
      prescreeningResults: selectedPrescreeningResults,
      onWaitlist,
      createdWithin: selectedCreatedWithin,
      createdAfter: selectedCreatedWithinDates.startDate || undefined,
      createdBefore: selectedCreatedWithinDates.endDate || undefined,
      showingWithin: selectedShowingWithin,
      showingAfter: selectedShowingWithinDates.startDate || undefined,
      showingBefore: selectedShowingWithinDates.endDate || undefined
    }));
    setShowFilters(false);
  };

  const currentFilters = useMemo(
    () => ({
      properties: selectedProperties,
      stages: selectedStages,
      leadSources: selectedLeadSources,
      tags: selectedTags,
      activityTypes: selectedActivityTypes
    }),
    [
      selectedProperties,
      selectedStages,
      selectedLeadSources,
      selectedTags,
      selectedActivityTypes
    ]
  );

  const hasSelectedFilters = useMemo(
    () =>
      Object.values(currentFilters).some(
        (filterArray) => filterArray.length > 0
      ) ||
      selectedApprovalLikelihoodBands.length > 0 ||
      onWaitlist ||
      selectedCreatedWithin !== CreatedWithinOption.None ||
      selectedShowingWithin !== ShowingWithinOption.None,
    [
      currentFilters,
      selectedApprovalLikelihoodBands,
      onWaitlist,
      selectedCreatedWithin,
      selectedShowingWithin
    ]
  );

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      open={showFilters}
      onClose={() => setShowFilters(false)}
      size={360}
    >
      <div className="flex flex-col h-full px-6">
        <div className="flex items-center justify-between py-4 border-b border-alpha-40">
          <h1 className="text-xl font-bold text-content-1000">Filters</h1>
          <IconButton
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            onClick={() => setShowFilters(false)}
            HugeIcon={Cancel01Icon}
          />
        </div>

        <Show if={hasSelectedFilters}>
          <div className="py-4 border-b border-alpha-40">
            <Button
              variant={ButtonVariant.TEXT}
              className="ml-2 text-base font-bold"
              onClick={() => {
                setSelectedProperties([]);
                setSelectedStages([]);
                setSelectedLeadSources([]);
                setSelectedTags([]);
                setSelectedActivityTypes([]);
                setSelectedApprovalLikelihoodBands([]);
                setOnWaitlist(false);
                setSelectedCreatedWithin(CreatedWithinOption.None);
                setSelectedCreatedWithinDates({ startDate: "", endDate: "" });
                setSelectedShowingWithin(ShowingWithinOption.None);
                setSelectedShowingWithinDates({ startDate: "", endDate: "" });
              }}
            >
              Unselect all
            </Button>
          </div>
        </Show>

        <Show
          if={!Object.values(filterOptions ?? {}).some((opt) => opt?.length)}
        >
          <p className="my-1 text-sm text-content-400">No options available</p>
        </Show>

        <div className="flex-1 overflow-y-auto divide-y divide-alpha-40">
          <Show
            if={
              !hidePropertyFilter && Boolean(filterOptions?.properties?.length)
            }
          >
            <FilterSection
              title="Property"
              options={
                filterOptions?.properties?.map((p) => ({
                  id: String(p.id),
                  name: p.name,
                  count: p.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedProperties.map(
                (property) => property.id
              )}
              onChange={(propertyId) =>
                toggleFilter(
                  propertyId,
                  filterOptions?.properties?.map((p) => ({
                    id: String(p.id),
                    name: p.name
                  })),
                  setSelectedProperties
                )
              }
            />
          </Show>
          <FilterSection
            title="Waitlist"
            showSearch={false}
            options={WAITLIST_FILTER_OPTIONS}
            selectedOptions={onWaitlist ? [WAITLIST_FILTER_OPTION_ID] : []}
            onChange={() => setOnWaitlist((prev) => !prev)}
          />
          <Show if={Boolean(filterOptions?.stages?.length)}>
            <FilterSection
              title="Stage"
              options={
                filterOptions?.stages?.map((s) => ({
                  id: String(s.id),
                  name: s.name,
                  count: s.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedStages.map((stage) => stage.id)}
              onChange={(stageId) =>
                toggleFilter(
                  stageId,
                  filterOptions?.stages?.map((s) => ({
                    id: String(s.id),
                    name: s.name
                  })),
                  setSelectedStages
                )
              }
            />
          </Show>
          <FilterSection
            title="Tags"
            options={tagOptions}
            optionLimit={5}
            formatLabel={keepTagName}
            emptyText="No tags yet. Add a tag to a lead and it will show up here."
            selectedOptions={selectedTags.map((tag) => tag.id)}
            onChange={(tagId) =>
              toggleFilter(tagId, tagOptions, setSelectedTags)
            }
          />
          <Show if={Boolean(filterOptions?.lead_sources?.length)}>
            <FilterSection
              title="Lead Source"
              options={
                filterOptions?.lead_sources?.map((ls) => ({
                  id: String(ls.id),
                  name: ls.name,
                  count: ls.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedLeadSources.map((ls) => ls.id)}
              onChange={(leadSourceId) =>
                toggleFilter(
                  leadSourceId,
                  filterOptions?.lead_sources?.map((ls) => ({
                    id: String(ls.id),
                    name: ls.name
                  })),
                  setSelectedLeadSources
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.activity_types?.length)}>
            <FilterSection
              title="Most recent activity"
              options={
                filterOptions?.activity_types?.map((at) => ({
                  name: at.activity_type,
                  count: at.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedActivityTypes}
              onChange={(activityType) => {
                setSelectedActivityTypes((prev) =>
                  prev.includes(activityType)
                    ? prev.filter((type) => type !== activityType)
                    : [...prev, activityType]
                );
              }}
            />
          </Show>
          <Show if={Boolean(isLeadScoringEnabled)}>
            <FilterSection
              title="Approval likelihood"
              options={APPROVAL_LIKELIHOOD_BAND_OPTIONS}
              showSearch={false}
              selectedOptions={selectedApprovalLikelihoodBands}
              onChange={(band) => {
                if (!isApprovalLikelihoodBand(band)) return;
                setSelectedApprovalLikelihoodBands((prev) =>
                  prev.includes(band)
                    ? prev.filter((item) => item !== band)
                    : [...prev, band]
                );
              }}
            />
          </Show>
          <FilterSection
            title="Prescreening result"
            options={prescreeningResultOptions}
            showSearch={false}
            selectedOptions={selectedPrescreeningResults}
            onChange={(result) => {
              const value = result as PrescreeningResult;
              setSelectedPrescreeningResults((prev) =>
                prev.includes(value)
                  ? prev.filter((item) => item !== value)
                  : [...prev, value]
              );
            }}
          />
          <div className="border-t border-alpha-40 py-4 space-y-2">
            <DateRangeSelect
              id="created_within"
              label="Created within"
              value={selectedCreatedWithin}
              startDate={selectedCreatedWithinDates.startDate}
              endDate={selectedCreatedWithinDates.endDate}
              presets={LEADS_DATE_PRESETS}
              onChange={(presetValue, start, end) => {
                setSelectedCreatedWithin(presetValue as CreatedWithinOption);
                setSelectedCreatedWithinDates({
                  startDate: start ?? "",
                  endDate: end ?? ""
                });
              }}
            />
            <DateRangeSelect
              id="showing_within"
              label="Showing date within"
              value={selectedShowingWithin}
              startDate={selectedShowingWithinDates.startDate}
              endDate={selectedShowingWithinDates.endDate}
              presets={SHOWING_DATE_PRESETS}
              onChange={(presetValue, start, end) => {
                setSelectedShowingWithin(presetValue as ShowingWithinOption);
                setSelectedShowingWithinDates({
                  startDate: start ?? "",
                  endDate: end ?? ""
                });
              }}
            />
          </div>
        </div>

        <div className="py-4 flex gap-2 border-t border-alpha-40">
          <Button
            className="w-[152px]"
            size={ButtonSize.BASE}
            onClick={() => setShowFilters(false)}
          >
            Cancel
          </Button>
          <Button
            className="w-[152px]"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
